import type {
  GpuBudget,
  GpuBudgetCatalog,
  GpuBudgetCatalogKind,
  GpuBudgetLine,
  GpuBudgetWarning,
} from '../../../../types/models/GpuBudget';
import { formatUnitPrice } from '../../../../utils/money';
import {
  BUDGET_GENERIC_ESTIMATE_NOTE,
  BUDGET_NOTHING_TO_ADD,
  BUDGET_STATUS_PRICE_UNAVAILABLE,
  BUDGET_STATUS_UNAVAILABLE,
} from './gpuBudgetCopy';

// The copy registry of the budget line kinds, plus the display helpers every section shares.
//
// The kinds themselves — their order, bounds, units, products and whether they are orderable — come
// from the backend catalog (`GET …/budgets/catalog`), never from here. This registry only adds nicer
// English wording for the kinds we know. A catalog kind with NO entry still renders and stays FULLY
// EDITABLE through the generic fallback: adding the next kind (e.g. "SLA 24/7-4H") needs at most one
// entry below, and none at all to work. Typed `Partial<Record<string, …>>` on purpose — never an
// exhaustive union: an unknown kind must type-check.
//
// `{disk_size_gb}` in a note is replaced by the catalog's value.

export interface KindCopy {
  /** Section title, subtotal label. */
  title: string;
  /** The section's Add button. */
  addLabel: string;
  /** The line modal's title when adding / editing. */
  addTitle: string;
  editTitle: string;
  /** Replaces the generic estimate note when the catalog kind is not orderable. */
  estimateNote?: string;
  /** Always shown under the section title (e.g. what a server includes). */
  note?: string;
  /** Column headers of the section table. */
  productHeader?: string;
  quantityHeader?: string;
  /** The quantity field's label in the line modal. */
  quantityLabel?: string;
  /** Shown when the section has no line yet. */
  emptyText?: string;
  /** The Add button's tooltip when the kind offers no product. */
  noProductsTooltip?: string;
  /**
   * Budget warning codes rendered INSIDE this section (in place of its estimate note) instead of the
   * warnings stack at the top.
   */
  sectionWarningCodes?: string[];
}

export const BUDGET_KIND_COPY: Partial<Record<string, KindCopy>> = {
  server: {
    title: 'Servers',
    addLabel: 'Add a server',
    addTitle: 'Add a server',
    editTitle: 'Edit server',
    note: 'Every server includes a {disk_size_gb} GB system disk, included in its price.',
    productHeader: 'Product',
    quantityHeader: 'Servers',
    quantityLabel: 'Number of servers',
    emptyText: 'No servers in this budget yet.',
  },
  storage: {
    title: 'Supplementary storage',
    addLabel: 'Add storage',
    addTitle: 'Add storage',
    editTitle: 'Edit storage',
    estimateNote:
      'Supplementary storage cannot be ordered yet. These lines are an estimate only. Every server already includes a {disk_size_gb} GB system disk.',
    productHeader: 'Type',
    quantityHeader: 'Size (GB)',
    quantityLabel: 'Size',
    emptyText: 'No supplementary storage in this budget yet.',
    noProductsTooltip: 'Supplementary storage is not available yet.',
    sectionWarningCodes: ['storage_not_orderable'],
  },
};

/** The copy of a kind, the registry entry merged over the generic fallback. */
export interface ResolvedKindCopy {
  title: string;
  addLabel: string;
  addTitle: string;
  editTitle: string;
  estimateNote: string | null;
  note: string | null;
  productHeader: string;
  quantityHeader: string;
  quantityLabel: string;
  emptyText: string;
  noProductsTooltip: string;
  sectionWarningCodes: string[];
  /** `false` for the generic fallback: its quantity cells carry their unit ("2 servers"). */
  isKnown: boolean;
}

/** `sla_x` → "Sla x": the key with `_` replaced by spaces and its first letter capitalised. */
export function fallbackKindTitle(kind: string): string {
  const spaced = kind.replace(/_/g, ' ').trim();
  return spaced === '' ? 'Item' : spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function kindCopy(kind: string): ResolvedKindCopy {
  const entry = BUDGET_KIND_COPY[kind];
  return {
    title: entry?.title ?? fallbackKindTitle(kind),
    addLabel: entry?.addLabel ?? 'Add item',
    addTitle: entry?.addTitle ?? 'Add item',
    editTitle: entry?.editTitle ?? 'Edit item',
    estimateNote: entry?.estimateNote ?? null,
    note: entry?.note ?? null,
    productHeader: entry?.productHeader ?? 'Product',
    quantityHeader: entry?.quantityHeader ?? 'Quantity',
    quantityLabel: entry?.quantityLabel ?? 'Quantity',
    emptyText: entry?.emptyText ?? 'Nothing in this budget yet.',
    noProductsTooltip: entry?.noProductsTooltip ?? BUDGET_NOTHING_TO_ADD,
    sectionWarningCodes: entry?.sectionWarningCodes ?? [],
    isKnown: entry !== undefined,
  };
}

/** Replace `{disk_size_gb}` with the catalog's value. */
export function interpolateCopy(template: string, catalog: Pick<GpuBudgetCatalog, 'disk_size_gb'>): string {
  return template.replace(/\{disk_size_gb\}/g, formatCount(catalog.disk_size_gb));
}

/** The note an unorderable section shows: the registry's, else the generic one. `null` when orderable. */
export function estimateNoteFor(kind: GpuBudgetCatalogKind, catalog: GpuBudgetCatalog): string | null {
  if (kind.orderable) {
    return null;
  }
  const note = kindCopy(kind.kind).estimateNote;
  return note !== null ? interpolateCopy(note, catalog) : BUDGET_GENERIC_ESTIMATE_NOTE;
}

/** An integer that is not money, fr-BE (`1500` → "1 500"). */
export function formatCount(n: number): string {
  return n.toLocaleString('fr-BE');
}

/**
 * The unit word for `n` of a catalog `quantity.unit` — keyed by unit, not by kind: `server` →
 * "server" / "servers", `gb` → "GB", an unknown unit → the raw string.
 */
export function unitWord(unit: string, n: number): string {
  switch (unit) {
    case 'server':
      return n === 1 ? 'server' : 'servers';
    case 'gb':
      return 'GB';
    default:
      return unit;
  }
}

/** `n` with its unit: "1 server", "3 servers", "1 500 GB"; the bare number when the unit is unknown (`null`). */
export function formatQuantity(unit: string | null, n: number): string {
  if (unit === null || unit === '') {
    return formatCount(n);
  }
  return `${formatCount(n)} ${unitWord(unit, n)}`;
}

/** The suffix of a unit price for its basis; none for a basis this frontend does not know. */
export function basisSuffix(basis: string | null): string {
  switch (basis) {
    case 'hour':
      return '/ h';
    case 'gb_month':
      return '/ GB / month';
    case 'month':
      return '/ month';
    default:
      return '';
  }
}

/** A 4-dp unit price with its basis suffix ("3,6932 € / h"). Formatting only. */
export function formatBasisPrice(value: string, basis: string | null): string {
  const suffix = basisSuffix(basis);
  return suffix === '' ? formatUnitPrice(value) : `${formatUnitPrice(value)} ${suffix}`;
}

/** Only `ok` lines carry amounts; any other status (known or not) reads as not available. */
export function isLineOk(line: Pick<GpuBudgetLine, 'status'>): boolean {
  return line.status === 'ok';
}

/** The label of a line that is not `ok`; `null` for an `ok` line. Unknown statuses read as unavailable. */
export function lineStatusLabel(line: Pick<GpuBudgetLine, 'status'>): string | null {
  if (isLineOk(line)) {
    return null;
  }
  return line.status === 'price_unavailable' ? BUDGET_STATUS_PRICE_UNAVAILABLE : BUDGET_STATUS_UNAVAILABLE;
}

/** The catalog kind of a key, or `null` when the catalog does not offer it (an "Other items" line). */
export function catalogKind(catalog: GpuBudgetCatalog, kind: string): GpuBudgetCatalogKind | null {
  return catalog.kinds.find((k) => k.kind === kind) ?? null;
}

/** Warning codes rendered inside a section present in the catalog, not in the stack at the top. */
export function sectionWarningCodes(catalog: GpuBudgetCatalog): Set<string> {
  const codes = new Set<string>();
  for (const kind of catalog.kinds) {
    kindCopy(kind.kind).sectionWarningCodes.forEach((code) => codes.add(code));
  }
  return codes;
}

/**
 * The warnings stack at the top of the tab, keyed by `code` and never showing a code twice: the
 * budget's budget-level warnings (`line_id === null`) in backend order, then each catalog warning whose
 * code is not shown yet. Codes a catalog section renders itself (e.g. `storage_not_orderable`) are
 * left out. With no budget, the catalog's warnings alone.
 */
export function warningStack(budget: GpuBudget | null, catalog: GpuBudgetCatalog): GpuBudgetWarning[] {
  const inSection = sectionWarningCodes(catalog);
  const seen = new Set<string>();
  const stack: GpuBudgetWarning[] = [];
  const candidates = [...(budget?.warnings.filter((w) => w.line_id === null) ?? []), ...catalog.warnings];
  for (const warning of candidates) {
    if (seen.has(warning.code) || inSection.has(warning.code)) {
      continue;
    }
    seen.add(warning.code);
    stack.push(warning);
  }
  return stack;
}

/** The first budget-level warning of `codes` in the budget (backend order), for a section. */
export function sectionWarning(budget: GpuBudget, codes: string[]): GpuBudgetWarning | null {
  return budget.warnings.find((w) => w.line_id === null && codes.includes(w.code)) ?? null;
}

/** A line's own warning (`line_unavailable`), shown on its row. */
export function lineWarning(budget: GpuBudget, line: GpuBudgetLine): GpuBudgetWarning | null {
  return budget.warnings.find((w) => w.line_id === line.id) ?? null;
}

/** How a line is named in prose: its product label, else the kind's title. */
export function lineName(line: GpuBudgetLine): string {
  return line.product?.label ?? kindCopy(line.kind).title;
}
